import { Component, DoCheck, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { IExportColumnInfo, IRow, IRowItem } from 'app/modules/reports/interfaces/reporting-data';
import { ReportingService } from 'app/modules/reports/services/reporting.service';
import {
    IDialogParams,
    ILoanProfitabilityDetail,
    ILoanProfitabilityDialogData,
    IProfitabilityData,
    IProfitabilityIncomeStatement,
} from '../../../incentives/interfaces/profitability-data';
import { ProfitabilityService } from '../../../incentives/services/profitability.service';
import { ReusableFunctions } from '../../classes/reusable-functions';
import { NoDataActionType, NoDataSourceType, NoDataTypes } from '../../interfaces/no-data';

@Component({
    selector: 'loan-profitability-dialog',
    templateUrl: './loan-profitability-dialog.component.html',
    styleUrls: ['./loan-profitability-dialog.component.scss'],
})
export class LoanProfitabilityDialogComponent implements OnInit, DoCheck {
    constructor(
        @Inject(MAT_DIALOG_DATA) public data: string,
        private router: Router,
        private profitabilityService: ProfitabilityService,
        private reusableFunctions: ReusableFunctions,
        private reportingService: ReportingService
    ) {}
    panelOpenState: boolean;
    dialogData: ILoanProfitabilityDialogData;
    isMainExansionDirty = false;
    dialogParameters: IDialogParams;
    hasLPData: boolean;
    isDataLoading: boolean;
    noDataType: NoDataTypes;
    hasSetDialogHeight: boolean;
    columnInfo: IExportColumnInfo[] = [];
    dataRows: IRow[] = [];

    ngOnInit() {
        this.hasSetDialogHeight = false;
        this.dialogData = {
            dialogHeaderData: {
                loanNumber: '',
                borrower: '',
                loanOriginator: '',
                branch: '',
                grossProfit: '',
                totalBps: '',
                color: '',
            },
            dialogExpansionData: [],
        };
        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: 'Loan Profitaility',
            inputSource: NoDataSourceType.table,
        };
        this.hasLPData = false;
        this.isDataLoading = true;
        this.dialogParameters = this.profitabilityService.dialogParametrs;
        this.getDialogData();
    }

    getDialogData() {
        if (this.dialogParameters && this.dialogParameters.params) {
            this.profitabilityService.getLoanGLDetails(this.dialogParameters.params).subscribe(
                (res) => {
                    if (res && res.length > 0) {
                        this.setDataInitialHorizontalLayout(res);
                        this.setGLDetailDialogData(res, this.dialogParameters.params?.loanNumber);
                        this.profitabilityService
                            .getLoanDetailsByLoanNumber(this.dialogParameters.params)
                            .subscribe((res) => {
                                if (res) {
                                    let headerData = this.dialogData.dialogHeaderData;
                                    headerData.loanOriginator = res.loanOriginator;
                                    headerData.branch = res.branch;
                                    headerData.borrower = res.borrower;
                                }
                            });
                        this.isDataLoading = false;
                        this.hasLPData = true;
                    } else {
                        this.isDataLoading = false;
                        this.hasLPData = false;
                    }
                },
                (error) => {
                    this.noDataType.action = NoDataActionType.error;
                    this.isDataLoading = false;
                    this.hasLPData = false;
                }
            );
        }
    }

    ngDoCheck() {
        if (!this.hasSetDialogHeight) {
            this.setDialogSize();
        }
    }

    setGLDetailDialogData(glInfo: IProfitabilityIncomeStatement[], loanNumber: string) {
        let netProfitTotals = glInfo.find((item) => item.level === 'NetProfit');
        let accountTypeInfo = glInfo.filter((item) => item.level === 'AccountType');
        let accountCatergoryInfo = glInfo.filter((item) => item.level === 'AccountCategory');
        let accountCatergoryDetails = glInfo.filter((item) => item.level === 'AccountName');

        if (netProfitTotals) {
            let dialogHeaderData = this.dialogData.dialogHeaderData;
            dialogHeaderData.loanNumber = loanNumber;
            dialogHeaderData.totalBps = this.formatBps(netProfitTotals.bps);
            dialogHeaderData.grossProfit = this.formatMoney(netProfitTotals.total);
            dialogHeaderData.color = this.getColor(netProfitTotals.total);
        }

        if (accountTypeInfo?.length > 0) {
            let dialogExpansionData = this.dialogData.dialogExpansionData;

            accountTypeInfo.forEach((typeInf) => {
                let expandData: ILoanProfitabilityDetail = {
                    mainGroupTitle: this.formatTitle(typeInf.accountType),
                    mainGroupAmountTotal: this.formatMoney(typeInf.total),
                    mainGroupBpsTotal: this.formatBps(typeInf.bps),
                    hasExpanded: false,
                    color: this.getColor(typeInf.total),
                    subGroupData: [],
                };

                let accountCatergoryGroup = accountCatergoryInfo.filter(
                    (catInfo) => catInfo.accountType === typeInf.accountType
                );
                let subGroup = expandData.subGroupData;
                if (accountCatergoryGroup?.length > 0) {
                    let accountCatergoryDetailDataByType = accountCatergoryDetails.filter(
                        (catDetail) => catDetail.accountType === typeInf.accountType
                    );

                    accountCatergoryGroup.forEach((catGrp) => {
                        let accountCatergoryDetailsByCategory =
                            accountCatergoryDetailDataByType.filter(
                                (detail) => detail.accountCategory === catGrp.accountCategory
                            );
                        let subGroupData: IProfitabilityData[] = [];
                        if (accountCatergoryDetailsByCategory?.length > 0) {
                            accountCatergoryDetailsByCategory.forEach((item) => {
                                subGroupData.push({
                                    title: item.accountName,
                                    color: this.getColor(item.total),
                                    bps: this.formatBps(item.bps),
                                    amount: this.formatMoney(item.total),
                                });
                            });
                        }

                        subGroup.push({
                            subGroupTitle: catGrp.accountCategory,
                            subGroupAmountTotal: this.formatMoney(catGrp.total),
                            subGroupBpsTotal: this.formatBps(catGrp.bps),
                            hasExpanded: false,
                            color: this.getColor(catGrp.total),
                            data: subGroupData,
                        });
                    });
                }
                dialogExpansionData.push(expandData);
            });
        }
    }

    formatMoney(value: number) {
        return this.reusableFunctions.formatNumber(value, 2, 'currency', true);
    }

    formatBps(value: number) {
        return this.reusableFunctions.formatNumber(value, 3, 'number', true);
    }

    formatTitle(value: string) {
        return value.replace('Expense', 'Expenses');
    }

    getColor(value: number) {
        if (value < 0) {
            return '#E0000E';
        }
        return '';
    }

    clickLoanNumber(loanNumber: string) {
        this.dialogParameters.isLoanProfitabilityDialog = true;
        this.router.navigate([{ outlets: { modal: 'loanDetails' + '/' + loanNumber } }]);
    }

    setDialogSize() {
        let container = document.getElementById('lp-data-detail-container');
        if (container) {
            let windowHeight = window.innerHeight;
            container.style.setProperty('max-height', (windowHeight * 0.65).toFixed(0) + 'px');
            this.hasSetDialogHeight = true;
        }
    }

    viewAllTransactions(loanNumber: string) {
        this.profitabilityService.setHasRouteHistory(true);
        this.router.navigate(['/reporting/financials/loan-transactions'], {
            queryParams: {
                loanNumber: loanNumber,
                titleSuffix: ': Loan # ' + loanNumber,
                navFromUrl: 'financials/insights/profitability',
            },
        });
    }

    setDataInitialHorizontalLayout(data) {
        data = data.filter((x) => x.accountType !== null);
        this.getColumnsInfo();
        this.setCollapsingLevels(data);
    }

    getColumnsInfo() {
        this.columnInfo = [
            {
                columnId: 1,
                columnName: '',
                columnIdName: 'description',
                dataType: 'String',
            },
            {
                columnId: 2,
                columnName: 'Volume',
                columnIdName: 'Volume',
                dataType: 'Currency',
                precision: 2,
            },
            {
                columnId: 3,
                columnName: 'BPS',
                columnIdName: 'BPS',
                dataType: 'Numeric',
                precision: 3,
            },
        ];
    }

    setCollapsingLevels(data: any[]) {
        let levelOneItems = this.reusableFunctions.getUniqueItemList(data, 'accountType');
        levelOneItems = levelOneItems.filter((x) => x !== null);
        let rowId = 1;
        let levelOneProcessedRows: IRow[] = [];
        levelOneItems.forEach((levelOne) => {
            let last2levelRowId = 0;
            let dataRows: IRow[] = [];
            let levelOneProcessedRow = this.setRowValues(
                [],
                'total',
                rowId++,
                1,
                true,
                levelOne,
                500,
                16
            );

            let levelOneData = data.filter((item) => item.accountType === levelOne);
            levelOneProcessedRow.outlineStart = rowId + 1;
            let levelTwoItems = this.reusableFunctions.getUniqueItemList(
                levelOneData,
                'accountCategory'
            );
            let levelTwoRows: IRow[] = [];
            levelTwoItems.forEach((levelTwo) => {
                let levelTwoProcessedRow = this.setRowValues(
                    [],
                    'total',
                    rowId++,
                    2,
                    true,
                    levelTwo,
                    500,
                    14
                );
                last2levelRowId = levelTwoProcessedRow.rowId;
                let levelTwoData = levelOneData.filter((item) => item.accountCategory === levelTwo);
                let levelThreeItems = this.reusableFunctions.getUniqueItemList(
                    levelTwoData,
                    'accountName'
                );
                levelTwoProcessedRow.outlineStart = rowId + 1;
                levelTwoProcessedRow.outlineEnd =
                    levelTwoProcessedRow.outlineStart + levelThreeItems.length - 1;

                let levelThreeRows: IRow[] = [];
                levelThreeItems = levelThreeItems.filter((x) => x !== null);
                levelThreeItems.forEach((levelThree) => {
                    let levelThreeData = levelTwoData.filter(
                        (item) => item.accountName === levelThree
                    );
                    let levelThreeProcessedRow = this.setRowValues(
                        levelThreeData,
                        'total',
                        rowId++,
                        3,
                        false,
                        levelThree,
                        400,
                        11
                    );
                    levelThreeRows.push(levelThreeProcessedRow);
                });
                this.calculateVerticalGroupTotals(levelTwoProcessedRow, levelThreeRows);
                levelTwoRows.push(levelTwoProcessedRow);
                dataRows = dataRows.concat(levelTwoProcessedRow, levelThreeRows);
            });

            let last2LevelRow = dataRows.find((r) => r.rowId === last2levelRowId);
            if (last2LevelRow) {
                last2LevelRow.outlineEnd = last2LevelRow.outlineEnd + 1;
            }
            levelOneProcessedRow.outlineEnd = rowId;
            this.calculateVerticalGroupTotals(levelOneProcessedRow, levelTwoRows);
            levelOneProcessedRows.push(levelOneProcessedRow);
            let placeHolderRowRow = this.setRowValues([], '', rowId++, 0, true, '', 200, 8);
            this.dataRows = this.dataRows.concat(levelOneProcessedRow, dataRows, placeHolderRowRow);
        });
        let lastTotalRow = this.setRowValues([], '', rowId++, 1, true, 'Total', 500, 16);
        lastTotalRow.outlineEnd = rowId;
        if (levelOneProcessedRows.length === 2) {
            this.calculateVerticalDifferenceOfTwoRows(
                levelOneProcessedRows[0],
                levelOneProcessedRows[1],
                lastTotalRow
            );
            this.dataRows.push(lastTotalRow);
        }
    }

    setRowValues(
        dataLine: any[],
        dataFieldId: string,
        rowId: number,
        rowLevel: number,
        setEmptyRow: boolean,
        rowDescription: string,
        dataPointFontWeight = 400,
        fontSize = 11
    ) {
        let row: IRow = {
            rowId: rowId,
            rowData: [],
            rowOutlineLevel: rowLevel,
            outlineStart: null,
            outlineEnd: null,
        };

        let endTotal = 0;
        this.columnInfo.forEach((col) => {
            let rowItem: any = {
                columnId: col.columnId,
                dataType: col.dataType,
                value: '0',
                precision: col.precision,
                style: {
                    fontWeight: dataPointFontWeight,
                    fontSize: fontSize,
                },
                isNegativeValueBad: true,
            };
            if (col.columnIdName === 'description') {
                rowItem.value = rowDescription;
            } else if (!setEmptyRow) {
                if (col.columnIdName === 'Volume') {
                    let value = dataLine[0].total;
                    rowItem.value = value.toString();
                    endTotal += value;
                    rowItem.valueIfNumeric = value;
                } else if (col.columnIdName === 'BPS') {
                    let value = dataLine[0].bps;
                    rowItem.value = value.toString();
                    endTotal += value;
                    rowItem.valueIfNumeric = value;
                }
            }
            row.rowData.push(rowItem);
        });
        let endColumn = this.columnInfo.find((c) => c.columnIdName === 'endTotal');
        let rowEndValueInfo = row.rowData.find((r) => r.columnId === endColumn?.columnId);

        if (rowEndValueInfo) {
            rowEndValueInfo.value = endTotal.toString();
            rowEndValueInfo.valueIfNumeric = endTotal;
        }
        return row;
    }

    calculateVerticalGroupTotals(parentRow: IRow, childRows: IRow[]) {
        let childRowItems: IRowItem[] = [];
        childRows.forEach((cRow) => {
            childRowItems = childRowItems.concat(cRow.rowData);
        });

        this.columnInfo.forEach((col) => {
            if (col.columnIdName !== 'description' && col.columnName != '') {
                let columnTotal = 0;
                childRowItems.forEach((cItm) => {
                    if (cItm.columnId === col.columnId) {
                        columnTotal += cItm.valueIfNumeric ?? 0;
                    }
                });

                let parentColumn = parentRow.rowData.find((r) => r.columnId === col.columnId);
                if (parentColumn) {
                    parentColumn.valueIfNumeric = columnTotal;
                    parentColumn.value = columnTotal.toString();
                }
            }
        });
    }

    calculateVerticalDifferenceOfTwoRows(
        subtractFromRow: IRow,
        subtractingRow: IRow,
        resultingRow
    ) {
        this.columnInfo.forEach((col) => {
            if (col.columnIdName !== 'description' && col.columnName != '') {
                let subtractFromCell = subtractFromRow.rowData.find(
                    (r) => r.columnId === col.columnId
                );
                let subtractingCell = subtractingRow.rowData.find(
                    (r) => r.columnId === col.columnId
                );
                let resultingCell = resultingRow.rowData.find((r) => r.columnId === col.columnId);

                if (subtractFromCell && subtractingCell && resultingCell) {
                    let difference =
                        subtractFromCell.valueIfNumeric - subtractingCell.valueIfNumeric;
                    resultingCell.valueIfNumeric = difference;
                    resultingCell.value = difference.toString();
                }
            }
        });
    }

    exportToExcel() {
        let exportName = 'Loan-Profitability-';
        let exportData = [];
        this.reportingService.setQuickExportData(
            this.columnInfo,
            exportData,
            exportName,
            true,
            this.dataRows
        );
    }
}
